import React, { useEffect, useState } from "react";
import axios from "../axios";
import "./StaffManagement.css";

// Fix API URL - ensure it ends with /api/
const getApiBase = () => {
  const envUrl = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";
  const cleanUrl = envUrl.replace(/\/$/, "");

  return cleanUrl.endsWith("/api") ? cleanUrl + "/" : cleanUrl + "/api/";
};

const API_BASE = getApiBase();

// Get base URL for media files (without /api/)
const getMediaBase = () => {
  const envUrl = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";
  return envUrl.replace(/\/$/, "");
};

const MEDIA_BASE = getMediaBase();

const ComprehensiveStaffManagement = () => {
  // Active tab state
  const [activeTab, setActiveTab] = useState("employees");

  // Employees State
  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [employeeFilters, setEmployeeFilters] = useState({
    status: "",
    gender: "",
  });

  // ✅ PREVIEW MODAL STATE (NEW)
  const [previewModal, setPreviewModal] = useState({
    isOpen: false,
    type: null, // 'image' or 'pdf'
    url: null,
    title: null,
  });

  // Qualifications State
  const [qualifications, setQualifications] = useState([]);
  const [qualificationsLoading, setQualificationsLoading] = useState(false);
  const [qualificationSearch, setQualificationSearch] = useState("");

  // Certificates State
  const [certificates, setCertificates] = useState([]);
  const [certificatesLoading, setCertificatesLoading] = useState(false);
  const [certificateSearch, setCertificateSearch] = useState("");
  const [certificateFilters, setCertificateFilters] = useState({
    verification_status: "",
  });

  // References State
  const [references, setReferences] = useState([]);
  const [referencesLoading, setReferencesLoading] = useState(false);
  const [referenceSearch, setReferenceSearch] = useState("");
  const [referenceFilters, setReferenceFilters] = useState({
    verification_status: "",
  });

  // Designations State
  const [designations, setDesignations] = useState([]);
  const [designationsLoading, setDesignationsLoading] = useState(false);
  const [designationSearch, setDesignationSearch] = useState("");

  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [modalType, setModalType] = useState("employee");
  const [selectedItem, setSelectedItem] = useState(null);

  // File uploads
  const [employeePhoto, setEmployeePhoto] = useState(null);
  const [employeePhotoPreview, setEmployeePhotoPreview] = useState(null);
  const [certificateFile, setCertificateFile] = useState(null);

  // Forms State
  const [employeeForm, setEmployeeForm] = useState({
    name: "",
    father_or_spouse_name: "",
    gender: "",
    dob: "",
    mobile: "",
    email: "",
    address_current: "",
    address_permanent: "",
    aadhaar: "",
    pan: "",
    date_of_joining: "",
    date_of_resignation: "",
    employee_status: "Active",
    notice_period_days: "",
    last_working_date: "",
    reason_for_exit: "",
    rehire_eligible: true,
    remarks: "",
  });

  const [qualificationForm, setQualificationForm] = useState({
    employee: "",
    qualification: "",
    specialization: "",
    college: "",
    university: "",
    year_of_passing: "",
    percentage_or_grade: "",
  });

  const [certificateForm, setCertificateForm] = useState({
    employee: "",
    qualification: "",
    certificate_name: "",
    certificate_type: "",
    issue_date: "",
    issued_by: "",
    verification_status: "Pending",
    remarks: "",
  });

  const [referenceForm, setReferenceForm] = useState({
    employee: "",
    reference_name: "",
    relationship: "",
    company_name: "",
    designation: "",
    mobile_number: "",
    email: "",
    address: "",
    years_known: "",
    verification_status: "Pending",
    remarks: "",
  });

  const [designationForm, setDesignationForm] = useState({
    employee: "",
    designation: "",
    department: "",
    start_date: "",
    end_date: "",
    is_current: true,
    remarks: "",
  });

  const token = localStorage.getItem("access_token");

  // Axios instance
  const api = React.useMemo(() => {
    console.log("🔧 API Base URL:", API_BASE);
    console.log("🔧 Media Base URL:", MEDIA_BASE);
    return axios.create({
      baseURL: API_BASE,
      headers: { Authorization: token ? `Bearer ${token}` : "" },
    });
  }, [token]);

  // ==================== PREVIEW FUNCTIONS (NEW) ====================
  
  const openPreview = (type, url, title) => {
    setPreviewModal({
      isOpen: true,
      type,
      url,
      title,
    });
  };

  const closePreview = () => {
    setPreviewModal({
      isOpen: false,
      type: null,
      url: null,
      title: null,
    });
  };

  // ==================== FETCH FUNCTIONS ====================
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        closePreview(); // ✅ Updated to use closePreview
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const fetchEmployees = async () => {
    setEmployeesLoading(true);
    try {
      const params = {};
      if (employeeSearch) params.search = employeeSearch;
      if (employeeFilters.status)
        params.employee_status = employeeFilters.status;
      if (employeeFilters.gender) params.gender = employeeFilters.gender;

      const res = await api.get("employees/", { params });
      const data = res.data.results || res.data;
      setEmployees(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching employees:", err);
      alert(
        "Error loading employees: " + (err.response?.status || err.message),
      );
      setEmployees([]);
    } finally {
      setEmployeesLoading(false);
    }
  };

  const fetchQualifications = async () => {
    setQualificationsLoading(true);
    try {
      const params = {};
      if (qualificationSearch) params.search = qualificationSearch;

      const res = await api.get("qualifications/", { params });
      const data = res.data.results || res.data;
      setQualifications(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching qualifications:", err);
      setQualifications([]);
    } finally {
      setQualificationsLoading(false);
    }
  };

  const fetchCertificates = async () => {
    setCertificatesLoading(true);
    try {
      const params = {};
      if (certificateSearch) params.search = certificateSearch;
      if (certificateFilters.verification_status)
        params.verification_status = certificateFilters.verification_status;

      const res = await api.get("certificates/", { params });
      const data = res.data.results || res.data;
      setCertificates(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching certificates:", err);
      setCertificates([]);
    } finally {
      setCertificatesLoading(false);
    }
  };

  const fetchReferences = async () => {
    setReferencesLoading(true);
    try {
      const params = {};
      if (referenceSearch) params.search = referenceSearch;
      if (referenceFilters.verification_status)
        params.verification_status = referenceFilters.verification_status;

      const res = await api.get("references/", { params });
      const data = res.data.results || res.data;
      setReferences(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching references:", err);
      setReferences([]);
    } finally {
      setReferencesLoading(false);
    }
  };

  const fetchDesignations = async () => {
    setDesignationsLoading(true);
    try {
      const params = {};
      if (designationSearch) params.search = designationSearch;

      const res = await api.get("designations/", { params });
      const data = res.data.results || res.data;
      setDesignations(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching designations:", err);
      setDesignations([]);
    } finally {
      setDesignationsLoading(false);
    }
  };

  useEffect(() => {
    switch (activeTab) {
      case "employees":
        fetchEmployees();
        break;
      case "qualifications":
        fetchQualifications();
        fetchEmployees();
        break;
      case "certificates":
        fetchCertificates();
        fetchEmployees();
        fetchQualifications();
        break;
      case "references":
        fetchReferences();
        fetchEmployees();
        break;
      case "designations":
        fetchDesignations();
        fetchEmployees();
        break;
      default:
        break;
    }
  }, [
    activeTab,
    employeeSearch,
    employeeFilters,
    qualificationSearch,
    certificateSearch,
    certificateFilters,
    referenceSearch,
    referenceFilters,
    designationSearch,
  ]);

  // ==================== MODAL FUNCTIONS ====================

  const openModal = (type, mode, item = null) => {
    setModalType(type);
    setModalMode(mode);
    setSelectedItem(item);

    if (mode === "add") {
      resetForms();
    } else if (mode === "edit" && item) {
      populateForm(type, item);
    }

    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForms();
    setSelectedItem(null);
  };

  const resetForms = () => {
    setEmployeeForm({
      name: "",
      father_or_spouse_name: "",
      gender: "",
      dob: "",
      mobile: "",
      email: "",
      address_current: "",
      address_permanent: "",
      aadhaar: "",
      pan: "",
      date_of_joining: "",
      date_of_resignation: "",
      employee_status: "Active",
      notice_period_days: "",
      last_working_date: "",
      reason_for_exit: "",
      rehire_eligible: true,
      remarks: "",
    });
    setQualificationForm({
      employee: "",
      qualification: "",
      specialization: "",
      college: "",
      university: "",
      year_of_passing: "",
      percentage_or_grade: "",
    });
    setCertificateForm({
      employee: "",
      qualification: "",
      certificate_name: "",
      certificate_type: "",
      issue_date: "",
      issued_by: "",
      verification_status: "Pending",
      remarks: "",
    });
    setReferenceForm({
      employee: "",
      reference_name: "",
      relationship: "",
      company_name: "",
      designation: "",
      mobile_number: "",
      email: "",
      address: "",
      years_known: "",
      verification_status: "Pending",
      remarks: "",
    });
    setDesignationForm({
      employee: "",
      designation: "",
      department: "",
      start_date: "",
      end_date: "",
      is_current: true,
      remarks: "",
    });
    setEmployeePhoto(null);
    setEmployeePhotoPreview(null);
    setCertificateFile(null);
  };

  const populateForm = (type, item) => {
    switch (type) {
      case "employee":
        setEmployeeForm({ ...item });
        setEmployeePhotoPreview(item.photo || null);
        break;
      case "qualification":
        setQualificationForm({ ...item });
        break;
      case "certificate":
        setCertificateForm({ ...item });
        break;
      case "reference":
        setReferenceForm({ ...item });
        break;
      case "designation":
        setDesignationForm({ ...item });
        break;
    }
  };

  // ==================== CRUD OPERATIONS ====================

  const saveEmployee = async () => {
    const formData = new FormData();
    
    // Clean the data - only append non-empty values
    Object.keys(employeeForm).forEach((key) => {
      const value = employeeForm[key];
      
      // Skip empty strings for optional fields
      if (value === "" || value === null || value === undefined) {
        return;
      }
      
      // For boolean fields, always append
      if (typeof value === 'boolean') {
        formData.append(key, value);
        return;
      }
      
      formData.append(key, value);
    });
    
    if (employeePhoto) formData.append("photo", employeePhoto);

    try {
      if (modalMode === "add") {
        await api.post("employees/", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        alert("Employee added successfully");
      } else {
        await api.patch(`employees/${selectedItem.employee_id}/`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        alert("Employee updated successfully");
      }
      closeModal();
      fetchEmployees();
    } catch (err) {
      console.error("Error saving employee:", err);
      alert(
        "Error: " +
          (err.response?.data
            ? JSON.stringify(err.response.data)
            : err.message),
      );
    }
  };

  const deleteEmployee = async (id) => {
    if (!window.confirm("Delete this employee?")) return;
    try {
      await api.delete(`employees/${id}/`);
      alert("Employee deleted");
      fetchEmployees();
    } catch (err) {
      alert("Error deleting employee");
    }
  };

  const saveQualification = async () => {
    try {
      // Clean the form data - remove empty strings
      const cleanedData = {};
      Object.keys(qualificationForm).forEach((key) => {
        const value = qualificationForm[key];
        if (value !== "" && value !== null && value !== undefined) {
          cleanedData[key] = value;
        }
      });

      if (modalMode === "add") {
        await api.post("qualifications/", cleanedData);
        alert("Qualification added successfully");
      } else {
        await api.patch(
          `qualifications/${selectedItem.id}/`,
          cleanedData,
        );
        alert("Qualification updated successfully");
      }
      closeModal();
      fetchQualifications();
    } catch (err) {
      console.error("Error saving qualification:", err);
      alert(
        "Error: " +
          (err.response?.data
            ? JSON.stringify(err.response.data)
            : err.message),
      );
    }
  };

  const deleteQualification = async (id) => {
    if (!window.confirm("Delete this qualification?")) return;
    try {
      await api.delete(`qualifications/${id}/`);
      alert("Qualification deleted");
      fetchQualifications();
    } catch (err) {
      alert("Error deleting qualification");
    }
  };

  const saveCertificate = async () => {
    const formData = new FormData();
    
    // Clean the data - only append non-empty values
    Object.keys(certificateForm).forEach((key) => {
      const value = certificateForm[key];
      
      // Skip empty strings for optional fields
      if (value === "" || value === null || value === undefined) {
        return;
      }
      
      formData.append(key, value);
    });
    
    if (certificateFile) formData.append("file", certificateFile);

    try {
      if (modalMode === "add") {
        await api.post("certificates/", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        alert("Certificate added successfully");
      } else {
        await api.patch(`certificates/${selectedItem.id}/`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        alert("Certificate updated successfully");
      }
      closeModal();
      fetchCertificates();
    } catch (err) {
      console.error("Error saving certificate:", err);
      alert(
        "Error: " +
          (err.response?.data
            ? JSON.stringify(err.response.data)
            : err.message),
      );
    }
  };

  const deleteCertificate = async (id) => {
    if (!window.confirm("Delete this certificate?")) return;
    try {
      await api.delete(`certificates/${id}/`);
      alert("Certificate deleted");
      fetchCertificates();
    } catch (err) {
      alert("Error deleting certificate");
    }
  };

  const verifyCertificate = async (id) => {
    try {
      await api.patch(`certificates/${id}/verify/`);
      alert("Certificate verified");
      fetchCertificates();
    } catch (err) {
      alert("Error verifying certificate");
    }
  };

  const saveReference = async () => {
    try {
      // Clean the form data - remove empty strings
      const cleanedData = {};
      Object.keys(referenceForm).forEach((key) => {
        const value = referenceForm[key];
        if (value !== "" && value !== null && value !== undefined) {
          cleanedData[key] = value;
        }
      });

      if (modalMode === "add") {
        await api.post("references/", cleanedData);
        alert("Reference added successfully");
      } else {
        await api.patch(`references/${selectedItem.id}/`, cleanedData);
        alert("Reference updated successfully");
      }
      closeModal();
      fetchReferences();
    } catch (err) {
      console.error("Error saving reference:", err);
      alert(
        "Error: " +
          (err.response?.data
            ? JSON.stringify(err.response.data)
            : err.message),
      );
    }
  };

  const deleteReference = async (id) => {
    if (!window.confirm("Delete this reference?")) return;
    try {
      await api.delete(`references/${id}/`);
      alert("Reference deleted");
      fetchReferences();
    } catch (err) {
      alert("Error deleting reference");
    }
  };

  const verifyReference = async (id) => {
    try {
      await api.patch(`references/${id}/verify/`);
      alert("Reference verified");
      fetchReferences();
    } catch (err) {
      alert("Error verifying reference");
    }
  };

  const saveDesignation = async () => {
    try {
      // Clean the form data - convert empty strings to null for date fields
      const cleanedData = {
        ...designationForm,
        end_date: designationForm.end_date || null,
        start_date: designationForm.start_date || null,
      };

      if (modalMode === "add") {
        await api.post("designations/", cleanedData);
        alert("Designation added successfully");
      } else {
        await api.patch(`designations/${selectedItem.id}/`, cleanedData);
        alert("Designation updated successfully");
      }
      closeModal();
      fetchDesignations();
    } catch (err) {
      console.error("Error saving designation:", err);
      alert(
        "Error: " +
          (err.response?.data
            ? JSON.stringify(err.response.data)
            : err.message),
      );
    }
  };

  const deleteDesignation = async (id) => {
    if (!window.confirm("Delete this designation?")) return;
    try {
      await api.delete(`designations/${id}/`);
      alert("Designation deleted");
      fetchDesignations();
    } catch (err) {
      alert("Error deleting designation");
    }
  };

  // ==================== HELPER FUNCTIONS ====================

  const getEmployeeName = (employeeId) => {
    const emp = employees.find((e) => e.employee_id === employeeId);
    return emp ? `${emp.employee_id} - ${emp.name}` : employeeId;
  };

  const getQualificationName = (qualId) => {
    const qual = qualifications.find((q) => q.id === qualId);
    return qual ? qual.qualification : qualId;
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEmployeePhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setEmployeePhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCertificateFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCertificateFile(file);
    }
  };

  // ==================== RENDER FUNCTIONS ====================

  const renderEmployeesTab = () => (
    <div>
      <div className="header">
        <h2>Employees Management</h2>
        <button
          className="btn-primary"
          onClick={() => openModal("employee", "add")}
        >
          ➕ Add Employee
        </button>
      </div>

      <div className="filters">
        <input
          type="text"
          placeholder="🔍 Search employees..."
          value={employeeSearch}
          onChange={(e) => setEmployeeSearch(e.target.value)}
          className="search-input"
        />
        <select
          value={employeeFilters.status}
          onChange={(e) =>
            setEmployeeFilters({ ...employeeFilters, status: e.target.value })
          }
          className="filter-select"
        >
          <option value="">All Status</option>
          <option value="Active">Active</option>
          <option value="Resigned">Resigned</option>
          <option value="Terminated">Terminated</option>
        </select>
        <select
          value={employeeFilters.gender}
          onChange={(e) =>
            setEmployeeFilters({ ...employeeFilters, gender: e.target.value })
          }
          className="filter-select"
        >
          <option value="">All Genders</option>
          <option value="Male">Male</option>
          <option value="Female">Female</option>
        </select>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Photo</th>
              <th>ID</th>
              <th>Name</th>
              <th>Mobile</th>
              <th>Email</th>
              <th>DOJ</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {employeesLoading ? (
              <tr>
                <td colSpan="8" className="text-center">
                  Loading...
                </td>
              </tr>
            ) : employees.length === 0 ? (
              <tr>
                <td colSpan="8" className="text-center">
                  No employees found
                </td>
              </tr>
            ) : (
              employees.map((emp) => (
                <tr key={emp.employee_id}>
                  <td>
                    {emp.photo ? (
                      <img
                        src={emp.photo}
                        alt="photo"
                        className="table-photo"
                        onClick={() => openPreview('image', emp.photo, `${emp.name} - Photo`)}
                        style={{ cursor: "pointer" }}
                      />
                    ) : (
                      <div className="no-photo">No Photo</div>
                    )}
                  </td>
                  <td>{emp.employee_id}</td>
                  <td>{emp.name}</td>
                  <td>{emp.mobile}</td>
                  <td>{emp.email || "N/A"}</td>
                  <td>{emp.date_of_joining}</td>
                  <td>
                    <span
                      className={`badge badge-${emp.employee_status.toLowerCase()}`}
                    >
                      {emp.employee_status}
                    </span>
                  </td>
                  <td>
                    <button
                      className="btn-sm btn-edit"
                      onClick={() => openModal("employee", "edit", emp)}
                    >
                      ✏️
                    </button>
                    <button
                      className="btn-sm btn-delete"
                      onClick={() => deleteEmployee(emp.employee_id)}
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderQualificationsTab = () => (
    <div>
      <div className="header">
        <h2>Qualifications Management</h2>
        <button
          className="btn-primary"
          onClick={() => openModal("qualification", "add")}
        >
          ➕ Add Qualification
        </button>
      </div>

      <div className="filters">
        <input
          type="text"
          placeholder="🔍 Search qualifications..."
          value={qualificationSearch}
          onChange={(e) => setQualificationSearch(e.target.value)}
          className="search-input"
        />
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Employee</th>
              <th>Qualification</th>
              <th>Specialization</th>
              <th>College/University</th>
              <th>Year</th>
              <th>Grade</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {qualificationsLoading ? (
              <tr>
                <td colSpan="7" className="text-center">
                  Loading...
                </td>
              </tr>
            ) : qualifications.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center">
                  No qualifications found
                </td>
              </tr>
            ) : (
              qualifications.map((qual) => (
                <tr key={qual.id}>
                  <td>{getEmployeeName(qual.employee)}</td>
                  <td>{qual.qualification}</td>
                  <td>{qual.specialization || "N/A"}</td>
                  <td>
                    {qual.college} {qual.university && `(${qual.university})`}
                  </td>
                  <td>{qual.year_of_passing}</td>
                  <td>{qual.percentage_or_grade}</td>
                  <td>
                    <button
                      className="btn-sm btn-edit"
                      onClick={() => openModal("qualification", "edit", qual)}
                    >
                      ✏️
                    </button>
                    <button
                      className="btn-sm btn-delete"
                      onClick={() => deleteQualification(qual.id)}
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderCertificatesTab = () => (
    <div>
      <div className="header">
        <h2>Certificates Management</h2>
        <button
          className="btn-primary"
          onClick={() => openModal("certificate", "add")}
        >
          ➕ Add Certificate
        </button>
      </div>

      <div className="filters">
        <input
          type="text"
          placeholder="🔍 Search certificates..."
          value={certificateSearch}
          onChange={(e) => setCertificateSearch(e.target.value)}
          className="search-input"
        />
        <select
          value={certificateFilters.verification_status}
          onChange={(e) =>
            setCertificateFilters({
              ...certificateFilters,
              verification_status: e.target.value,
            })
          }
          className="filter-select"
        >
          <option value="">All Status</option>
          <option value="Pending">Pending</option>
          <option value="Verified">Verified</option>
        </select>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Employee</th>
              <th>Certificate Name</th>
              <th>Type</th>
              <th>Issued By</th>
              <th>Issue Date</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {certificatesLoading ? (
              <tr>
                <td colSpan="7" className="text-center">
                  Loading...
                </td>
              </tr>
            ) : certificates.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center">
                  No certificates found
                </td>
              </tr>
            ) : (
              certificates.map((cert) => (
                <tr key={cert.id}>
                  <td>{getEmployeeName(cert.employee)}</td>
                  <td>{cert.certificate_name}</td>
                  <td>{cert.certificate_type}</td>
                  <td>{cert.issued_by || "N/A"}</td>
                  <td>{cert.issue_date || "N/A"}</td>
                  <td>
                    <span
                      className={`badge badge-${cert.verification_status.toLowerCase()}`}
                    >
                      {cert.verification_status}
                    </span>
                  </td>
                  <td>
                    {cert.verification_status === "Pending" && (
                      <button
                        className="btn-sm btn-success"
                        onClick={() => verifyCertificate(cert.id)}
                      >
                        ✓
                      </button>
                    )}
                    <button
                      className="btn-sm btn-edit"
                      onClick={() => openModal("certificate", "edit", cert)}
                    >
                      ✏️
                    </button>
                    <button
                      className="btn-sm btn-delete"
                      onClick={() => deleteCertificate(cert.id)}
                    >
                      🗑️
                    </button>
                    {cert.file && (
                      <button
                        className="btn-sm btn-view"
                        onClick={() => openPreview('pdf', cert.file, `${cert.certificate_name} - Certificate`)}
                      >
                        📄
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderReferencesTab = () => (
    <div>
      <div className="header">
        <h2>References Management</h2>
        <button
          className="btn-primary"
          onClick={() => openModal("reference", "add")}
        >
          ➕ Add Reference
        </button>
      </div>

      <div className="filters">
        <input
          type="text"
          placeholder="🔍 Search references..."
          value={referenceSearch}
          onChange={(e) => setReferenceSearch(e.target.value)}
          className="search-input"
        />
        <select
          value={referenceFilters.verification_status}
          onChange={(e) =>
            setReferenceFilters({
              ...referenceFilters,
              verification_status: e.target.value,
            })
          }
          className="filter-select"
        >
          <option value="">All Status</option>
          <option value="Pending">Pending</option>
          <option value="Verified">Verified</option>
        </select>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Employee</th>
              <th>Reference Name</th>
              <th>Relationship</th>
              <th>Company</th>
              <th>Mobile</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {referencesLoading ? (
              <tr>
                <td colSpan="7" className="text-center">
                  Loading...
                </td>
              </tr>
            ) : references.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center">
                  No references found
                </td>
              </tr>
            ) : (
              references.map((ref) => (
                <tr key={ref.id}>
                  <td>{getEmployeeName(ref.employee)}</td>
                  <td>{ref.reference_name}</td>
                  <td>{ref.relationship}</td>
                  <td>{ref.company_name || "N/A"}</td>
                  <td>{ref.mobile_number}</td>
                  <td>
                    <span
                      className={`badge badge-${ref.verification_status.toLowerCase()}`}
                    >
                      {ref.verification_status}
                    </span>
                  </td>
                  <td>
                    {ref.verification_status === "Pending" && (
                      <button
                        className="btn-sm btn-success"
                        onClick={() => verifyReference(ref.id)}
                      >
                        ✓
                      </button>
                    )}
                    <button
                      className="btn-sm btn-edit"
                      onClick={() => openModal("reference", "edit", ref)}
                    >
                      ✏️
                    </button>
                    <button
                      className="btn-sm btn-delete"
                      onClick={() => deleteReference(ref.id)}
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderDesignationsTab = () => (
    <div>
      <div className="header">
        <h2>Designations Management</h2>
        <button
          className="btn-primary"
          onClick={() => openModal("designation", "add")}
        >
          ➕ Add Designation
        </button>
      </div>

      <div className="filters">
        <input
          type="text"
          placeholder="🔍 Search designations..."
          value={designationSearch}
          onChange={(e) => setDesignationSearch(e.target.value)}
          className="search-input"
        />
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Employee</th>
              <th>Designation</th>
              <th>Department</th>
              <th>Start Date</th>
              <th>End Date</th>
              <th>Current</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {designationsLoading ? (
              <tr>
                <td colSpan="7" className="text-center">
                  Loading...
                </td>
              </tr>
            ) : designations.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center">
                  No designations found
                </td>
              </tr>
            ) : (
              designations.map((desig) => (
                <tr key={desig.id}>
                  <td>{getEmployeeName(desig.employee)}</td>
                  <td>{desig.designation}</td>
                  <td>{desig.department || "N/A"}</td>
                  <td>{desig.start_date}</td>
                  <td>{desig.end_date || "N/A"}</td>
                  <td>
                    {desig.is_current ? (
                      <span className="badge badge-active">Current</span>
                    ) : (
                      <span className="badge badge-resigned">Past</span>
                    )}
                  </td>
                  <td>
                    <button
                      className="btn-sm btn-edit"
                      onClick={() => openModal("designation", "edit", desig)}
                    >
                      ✏️
                    </button>
                    <button
                      className="btn-sm btn-delete"
                      onClick={() => deleteDesignation(desig.id)}
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  // ==================== MODAL FORMS ====================

  const renderEmployeeForm = () => (
    <div className="form-grid">
      <div className="form-group">
        <label>Name *</label>
        <input
          type="text"
          value={employeeForm.name}
          onChange={(e) =>
            setEmployeeForm({ ...employeeForm, name: e.target.value })
          }
          required
        />
      </div>

      <div className="form-group">
        <label>Father/Spouse Name</label>
        <input
          type="text"
          value={employeeForm.father_or_spouse_name}
          onChange={(e) =>
            setEmployeeForm({
              ...employeeForm,
              father_or_spouse_name: e.target.value,
            })
          }
        />
      </div>

      <div className="form-group">
        <label>Gender *</label>
        <select
          value={employeeForm.gender}
          onChange={(e) =>
            setEmployeeForm({ ...employeeForm, gender: e.target.value })
          }
          required
        >
          <option value="">Select Gender</option>
          <option value="Male">Male</option>
          <option value="Female">Female</option>
          <option value="Other">Other</option>
        </select>
      </div>

      <div className="form-group">
        <label>Date of Birth *</label>
        <input
          type="date"
          value={employeeForm.dob}
          onChange={(e) =>
            setEmployeeForm({ ...employeeForm, dob: e.target.value })
          }
          required
        />
      </div>

      <div className="form-group">
        <label>Mobile *</label>
        <input
          type="tel"
          value={employeeForm.mobile}
          onChange={(e) =>
            setEmployeeForm({ ...employeeForm, mobile: e.target.value })
          }
          required
        />
      </div>

      <div className="form-group">
        <label>Email</label>
        <input
          type="email"
          value={employeeForm.email}
          onChange={(e) =>
            setEmployeeForm({ ...employeeForm, email: e.target.value })
          }
        />
      </div>

      <div className="form-group full-width">
        <label>Current Address</label>
        <textarea
          value={employeeForm.address_current}
          onChange={(e) =>
            setEmployeeForm({
              ...employeeForm,
              address_current: e.target.value,
            })
          }
          rows="2"
        />
      </div>

      <div className="form-group full-width">
        <label>Permanent Address</label>
        <textarea
          value={employeeForm.address_permanent}
          onChange={(e) =>
            setEmployeeForm({
              ...employeeForm,
              address_permanent: e.target.value,
            })
          }
          rows="2"
        />
      </div>

      <div className="form-group">
        <label>Aadhaar Number</label>
        <input
          type="text"
          value={employeeForm.aadhaar}
          onChange={(e) =>
            setEmployeeForm({ ...employeeForm, aadhaar: e.target.value })
          }
        />
      </div>

      <div className="form-group">
        <label>PAN Number</label>
        <input
          type="text"
          value={employeeForm.pan}
          onChange={(e) =>
            setEmployeeForm({ ...employeeForm, pan: e.target.value })
          }
        />
      </div>

      <div className="form-group">
        <label>Date of Joining *</label>
        <input
          type="date"
          value={employeeForm.date_of_joining}
          onChange={(e) =>
            setEmployeeForm({
              ...employeeForm,
              date_of_joining: e.target.value,
            })
          }
          required
        />
      </div>

      <div className="form-group">
        <label>Employee Status *</label>
        <select
          value={employeeForm.employee_status}
          onChange={(e) =>
            setEmployeeForm({
              ...employeeForm,
              employee_status: e.target.value,
            })
          }
          required
        >
          <option value="Active">Active</option>
          <option value="Resigned">Resigned</option>
          <option value="Terminated">Terminated</option>
        </select>
      </div>

      {employeeForm.employee_status !== "Active" && (
        <>
          <div className="form-group">
            <label>Date of Resignation</label>
            <input
              type="date"
              value={employeeForm.date_of_resignation}
              onChange={(e) =>
                setEmployeeForm({
                  ...employeeForm,
                  date_of_resignation: e.target.value,
                })
              }
            />
          </div>

          <div className="form-group">
            <label>Notice Period (Days)</label>
            <input
              type="number"
              value={employeeForm.notice_period_days}
              onChange={(e) =>
                setEmployeeForm({
                  ...employeeForm,
                  notice_period_days: e.target.value,
                })
              }
            />
          </div>

          <div className="form-group">
            <label>Last Working Date</label>
            <input
              type="date"
              value={employeeForm.last_working_date}
              onChange={(e) =>
                setEmployeeForm({
                  ...employeeForm,
                  last_working_date: e.target.value,
                })
              }
            />
          </div>

          <div className="form-group">
            <label>Reason for Exit</label>
            <input
              type="text"
              value={employeeForm.reason_for_exit}
              onChange={(e) =>
                setEmployeeForm({
                  ...employeeForm,
                  reason_for_exit: e.target.value,
                })
              }
            />
          </div>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={employeeForm.rehire_eligible}
                onChange={(e) =>
                  setEmployeeForm({
                    ...employeeForm,
                    rehire_eligible: e.target.checked,
                  })
                }
              />{" "}
              Rehire Eligible
            </label>
          </div>
        </>
      )}

      <div className="form-group full-width">
        <label>Photo</label>
        <input type="file" accept="image/*" onChange={handlePhotoChange} />
        {employeePhotoPreview && (
          <img
            src={employeePhotoPreview}
            alt="Preview"
            className="photo-preview"
          />
        )}
      </div>

      <div className="form-group full-width">
        <label>Remarks</label>
        <textarea
          value={employeeForm.remarks}
          onChange={(e) =>
            setEmployeeForm({ ...employeeForm, remarks: e.target.value })
          }
          rows="3"
        />
      </div>
    </div>
  );

  const renderQualificationForm = () => (
    <div className="form-grid">
      <div className="form-group">
        <label>Employee *</label>
        <select
          value={qualificationForm.employee}
          onChange={(e) =>
            setQualificationForm({
              ...qualificationForm,
              employee: e.target.value,
            })
          }
          required
        >
          <option value="">Select Employee</option>
          {employees.map((emp) => (
            <option key={emp.employee_id} value={emp.employee_id}>
              {emp.employee_id} - {emp.name}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>Qualification *</label>
        <input
          type="text"
          value={qualificationForm.qualification}
          onChange={(e) =>
            setQualificationForm({
              ...qualificationForm,
              qualification: e.target.value,
            })
          }
          placeholder="e.g., B.Tech, M.Sc"
          required
        />
      </div>

      <div className="form-group">
        <label>Specialization</label>
        <input
          type="text"
          value={qualificationForm.specialization}
          onChange={(e) =>
            setQualificationForm({
              ...qualificationForm,
              specialization: e.target.value,
            })
          }
          placeholder="e.g., Computer Science"
        />
      </div>

      <div className="form-group">
        <label>College *</label>
        <input
          type="text"
          value={qualificationForm.college}
          onChange={(e) =>
            setQualificationForm({
              ...qualificationForm,
              college: e.target.value,
            })
          }
          required
        />
      </div>

      <div className="form-group">
        <label>University</label>
        <input
          type="text"
          value={qualificationForm.university}
          onChange={(e) =>
            setQualificationForm({
              ...qualificationForm,
              university: e.target.value,
            })
          }
        />
      </div>

      <div className="form-group">
        <label>Year of Passing *</label>
        <input
          type="number"
          value={qualificationForm.year_of_passing}
          onChange={(e) =>
            setQualificationForm({
              ...qualificationForm,
              year_of_passing: e.target.value,
            })
          }
          min="1950"
          max="2099"
          required
        />
      </div>

      <div className="form-group">
        <label>Percentage/Grade *</label>
        <input
          type="text"
          value={qualificationForm.percentage_or_grade}
          onChange={(e) =>
            setQualificationForm({
              ...qualificationForm,
              percentage_or_grade: e.target.value,
            })
          }
          placeholder="e.g., 85% or A+"
          required
        />
      </div>
    </div>
  );

  const renderCertificateForm = () => (
    <div className="form-grid">
      <div className="form-group">
        <label>Employee *</label>
        <select
          value={certificateForm.employee}
          onChange={(e) =>
            setCertificateForm({ ...certificateForm, employee: e.target.value })
          }
          required
        >
          <option value="">Select Employee</option>
          {employees.map((emp) => (
            <option key={emp.employee_id} value={emp.employee_id}>
              {emp.employee_id} - {emp.name}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>Qualification (Optional)</label>
        <select
          value={certificateForm.qualification}
          onChange={(e) =>
            setCertificateForm({
              ...certificateForm,
              qualification: e.target.value,
            })
          }
        >
          <option value="">Select Qualification</option>
          {qualifications
            .filter((q) => q.employee === certificateForm.employee)
            .map((qual) => (
              <option key={qual.id} value={qual.id}>
                {qual.qualification}
              </option>
            ))}
        </select>
      </div>

      <div className="form-group">
        <label>Certificate Name *</label>
        <input
          type="text"
          value={certificateForm.certificate_name}
          onChange={(e) =>
            setCertificateForm({
              ...certificateForm,
              certificate_name: e.target.value,
            })
          }
          required
        />
      </div>

      <div className="form-group">
        <label>Certificate Type *</label>
        <input
          type="text"
          value={certificateForm.certificate_type}
          onChange={(e) =>
            setCertificateForm({
              ...certificateForm,
              certificate_type: e.target.value,
            })
          }
          placeholder="e.g., Degree, Training, Award"
          required
        />
      </div>

      <div className="form-group">
        <label>Issue Date</label>
        <input
          type="date"
          value={certificateForm.issue_date}
          onChange={(e) =>
            setCertificateForm({
              ...certificateForm,
              issue_date: e.target.value,
            })
          }
        />
      </div>

      <div className="form-group">
        <label>Issued By</label>
        <input
          type="text"
          value={certificateForm.issued_by}
          onChange={(e) =>
            setCertificateForm({
              ...certificateForm,
              issued_by: e.target.value,
            })
          }
        />
      </div>

      <div className="form-group">
        <label>Verification Status *</label>
        <select
          value={certificateForm.verification_status}
          onChange={(e) =>
            setCertificateForm({
              ...certificateForm,
              verification_status: e.target.value,
            })
          }
          required
        >
          <option value="Pending">Pending</option>
          <option value="Verified">Verified</option>
        </select>
      </div>

      <div className="form-group full-width">
        <label>Certificate File</label>
        <input
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          onChange={handleCertificateFileChange}
        />
      </div>

      <div className="form-group full-width">
        <label>Remarks</label>
        <textarea
          value={certificateForm.remarks}
          onChange={(e) =>
            setCertificateForm({ ...certificateForm, remarks: e.target.value })
          }
          rows="3"
        />
      </div>
    </div>
  );

  const renderReferenceForm = () => (
    <div className="form-grid">
      <div className="form-group">
        <label>Employee *</label>
        <select
          value={referenceForm.employee}
          onChange={(e) =>
            setReferenceForm({ ...referenceForm, employee: e.target.value })
          }
          required
        >
          <option value="">Select Employee</option>
          {employees.map((emp) => (
            <option key={emp.employee_id} value={emp.employee_id}>
              {emp.employee_id} - {emp.name}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>Reference Name *</label>
        <input
          type="text"
          value={referenceForm.reference_name}
          onChange={(e) =>
            setReferenceForm({
              ...referenceForm,
              reference_name: e.target.value,
            })
          }
          required
        />
      </div>

      <div className="form-group">
        <label>Relationship *</label>
        <input
          type="text"
          value={referenceForm.relationship}
          onChange={(e) =>
            setReferenceForm({ ...referenceForm, relationship: e.target.value })
          }
          placeholder="e.g., Former Manager, Colleague"
          required
        />
      </div>

      <div className="form-group">
        <label>Company Name</label>
        <input
          type="text"
          value={referenceForm.company_name}
          onChange={(e) =>
            setReferenceForm({ ...referenceForm, company_name: e.target.value })
          }
        />
      </div>

      <div className="form-group">
        <label>Designation</label>
        <input
          type="text"
          value={referenceForm.designation}
          onChange={(e) =>
            setReferenceForm({ ...referenceForm, designation: e.target.value })
          }
        />
      </div>

      <div className="form-group">
        <label>Mobile Number *</label>
        <input
          type="tel"
          value={referenceForm.mobile_number}
          onChange={(e) =>
            setReferenceForm({
              ...referenceForm,
              mobile_number: e.target.value,
            })
          }
          required
        />
      </div>

      <div className="form-group">
        <label>Email</label>
        <input
          type="email"
          value={referenceForm.email}
          onChange={(e) =>
            setReferenceForm({ ...referenceForm, email: e.target.value })
          }
        />
      </div>

      <div className="form-group full-width">
        <label>Address</label>
        <textarea
          value={referenceForm.address}
          onChange={(e) =>
            setReferenceForm({ ...referenceForm, address: e.target.value })
          }
          rows="2"
        />
      </div>

      <div className="form-group">
        <label>Years Known</label>
        <input
          type="number"
          value={referenceForm.years_known}
          onChange={(e) =>
            setReferenceForm({ ...referenceForm, years_known: e.target.value })
          }
          min="0"
        />
      </div>

      <div className="form-group">
        <label>Verification Status *</label>
        <select
          value={referenceForm.verification_status}
          onChange={(e) =>
            setReferenceForm({
              ...referenceForm,
              verification_status: e.target.value,
            })
          }
          required
        >
          <option value="Pending">Pending</option>
          <option value="Verified">Verified</option>
        </select>
      </div>

      <div className="form-group full-width">
        <label>Remarks</label>
        <textarea
          value={referenceForm.remarks}
          onChange={(e) =>
            setReferenceForm({ ...referenceForm, remarks: e.target.value })
          }
          rows="3"
        />
      </div>
    </div>
  );

  const renderDesignationForm = () => (
    <div className="form-grid">
      <div className="form-group">
        <label>Employee *</label>
        <select
          value={designationForm.employee}
          onChange={(e) =>
            setDesignationForm({ ...designationForm, employee: e.target.value })
          }
          required
        >
          <option value="">Select Employee</option>
          {employees.map((emp) => (
            <option key={emp.employee_id} value={emp.employee_id}>
              {emp.employee_id} - {emp.name}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>Designation *</label>
        <input
          type="text"
          value={designationForm.designation}
          onChange={(e) =>
            setDesignationForm({
              ...designationForm,
              designation: e.target.value,
            })
          }
          placeholder="e.g., Software Engineer, Manager"
          required
        />
      </div>

      <div className="form-group">
        <label>Department</label>
        <input
          type="text"
          value={designationForm.department}
          onChange={(e) =>
            setDesignationForm({
              ...designationForm,
              department: e.target.value,
            })
          }
          placeholder="e.g., IT, HR, Sales"
        />
      </div>

      <div className="form-group">
        <label>Start Date *</label>
        <input
          type="date"
          value={designationForm.start_date}
          onChange={(e) =>
            setDesignationForm({
              ...designationForm,
              start_date: e.target.value,
            })
          }
          required
        />
      </div>

      <div className="form-group">
        <label>End Date</label>
        <input
          type="date"
          value={designationForm.end_date}
          onChange={(e) =>
            setDesignationForm({ ...designationForm, end_date: e.target.value })
          }
        />
      </div>

      <div className="form-group">
        <label>
          <input
            type="checkbox"
            checked={designationForm.is_current}
            onChange={(e) =>
              setDesignationForm({
                ...designationForm,
                is_current: e.target.checked,
              })
            }
          />{" "}
          Current Designation
        </label>
      </div>

      <div className="form-group full-width">
        <label>Remarks</label>
        <textarea
          value={designationForm.remarks}
          onChange={(e) =>
            setDesignationForm({ ...designationForm, remarks: e.target.value })
          }
          rows="3"
        />
      </div>
    </div>
  );

  const renderModalContent = () => {
    switch (modalType) {
      case "employee":
        return renderEmployeeForm();
      case "qualification":
        return renderQualificationForm();
      case "certificate":
        return renderCertificateForm();
      case "reference":
        return renderReferenceForm();
      case "designation":
        return renderDesignationForm();
      default:
        return null;
    }
  };

  const handleSave = () => {
    switch (modalType) {
      case "employee":
        saveEmployee();
        break;
      case "qualification":
        saveQualification();
        break;
      case "certificate":
        saveCertificate();
        break;
      case "reference":
        saveReference();
        break;
      case "designation":
        saveDesignation();
        break;
    }
  };

  return (
    <div className="comprehensive-staff-management">
      <h1>Comprehensive Staff Management System</h1>

      <div className="tabs">
        <button
          className={`tab ${activeTab === "employees" ? "active" : ""}`}
          onClick={() => setActiveTab("employees")}
        >
          👥 Employees
        </button>
        <button
          className={`tab ${activeTab === "qualifications" ? "active" : ""}`}
          onClick={() => setActiveTab("qualifications")}
        >
          🎓 Qualifications
        </button>
        <button
          className={`tab ${activeTab === "certificates" ? "active" : ""}`}
          onClick={() => setActiveTab("certificates")}
        >
          📜 Certificates
        </button>
        <button
          className={`tab ${activeTab === "references" ? "active" : ""}`}
          onClick={() => setActiveTab("references")}
        >
          📞 References
        </button>
        <button
          className={`tab ${activeTab === "designations" ? "active" : ""}`}
          onClick={() => setActiveTab("designations")}
        >
          💼 Designations
        </button>
      </div>

      <div className="tab-content">
        {activeTab === "employees" && renderEmployeesTab()}
        {activeTab === "qualifications" && renderQualificationsTab()}
        {activeTab === "certificates" && renderCertificatesTab()}
        {activeTab === "references" && renderReferencesTab()}
        {activeTab === "designations" && renderDesignationsTab()}
      </div>

      {/* ✅ MAIN EDIT/ADD MODAL */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {modalMode === "add" ? "Add" : "Edit"}{" "}
                {modalType.charAt(0).toUpperCase() + modalType.slice(1)}
              </h3>
              <button className="close-btn" onClick={closeModal}>
                ✕
              </button>
            </div>
            <div className="modal-body">{renderModalContent()}</div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={closeModal}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleSave}>
                {modalMode === "add" ? "Add" : "Update"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ NEW: PREVIEW MODAL FOR IMAGES & PDFs */}
      {previewModal.isOpen && (
        <div className="preview-modal-overlay" onClick={closePreview}>
          <div className="preview-modal-header">
            <h3 className="preview-modal-title">{previewModal.title}</h3>
            <button className="preview-close-btn" onClick={closePreview}>
              ✕
            </button>
          </div>
          
          <div className="preview-content" onClick={(e) => e.stopPropagation()}>
            {previewModal.type === 'image' ? (
              <img
                src={previewModal.url}
                alt={previewModal.title}
                className="preview-image"
              />
            ) : previewModal.type === 'pdf' ? (
              <iframe
                src={previewModal.url}
                title={previewModal.title}
                className="preview-pdf"
              />
            ) : null}
          </div>

          {previewModal.type === 'pdf' && (
            <a
              href={previewModal.url}
              download
              className="preview-download-btn"
              onClick={(e) => e.stopPropagation()}
            >
              📥 Download PDF
            </a>
          )}

          <div className="preview-instructions">
            Press ESC or click outside to close
          </div>
        </div>
      )}
    </div>
  );
};

export default ComprehensiveStaffManagement;